export type Money = { amount: number; currency: 'PLN' };

export function money(grosze: number): Money {
  if (!Number.isSafeInteger(grosze)) {
    throw new Error(`Kwota w groszach musi być liczbą całkowitą: ${grosze}`);
  }
  return { amount: grosze, currency: 'PLN' };
}

export function addMoney(a: Money, b: Money): Money {
  return money(a.amount + b.amount);
}

/** Round-half-to-even of num/den (den > 0), exact via BigInt. The one rounding rule for money. */
export function roundRatio(num: bigint, den: bigint): bigint {
  if (den <= 0n) throw new Error(`Mianownik musi być dodatni: ${den}`);
  const neg = num < 0n;
  const n = neg ? -num : num;
  let q = n / den;
  const r2 = (n % den) * 2n;
  if (r2 > den || (r2 === den && q % 2n === 1n)) q += 1n;
  return neg ? -q : q;
}

/** Exact rational (num/den) from the shortest decimal representation of a finite number. */
function toRatio(x: number): [bigint, bigint] {
  if (!Number.isFinite(x)) throw new Error(`Nieprawidłowy mnożnik: ${x}`);
  const m = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]\d+))?$/.exec(x.toString());
  if (!m) throw new Error(`Nieprawidłowy mnożnik: ${x}`);
  const frac = m[3] ?? '';
  let num = BigInt(`${m[1]}${m[2]}${frac}`);
  const exp = Number(m[4] ?? 0) - frac.length;
  let den = 1n;
  if (exp >= 0) num *= 10n ** BigInt(exp);
  else den = 10n ** BigInt(-exp);
  return [num, den];
}

export function multiplyMoney(m: Money, factor: number): Money {
  const [n, d] = toRatio(factor);
  return money(Number(roundRatio(BigInt(m.amount) * n, d)));
}

/** 1% = 100 bp. Half-even rounding of amount * bp / 10000. */
export function applyBasisPoints(m: Money, bp: number): Money {
  if (!Number.isSafeInteger(bp)) throw new Error(`Punkty bazowe muszą być liczbą całkowitą: ${bp}`);
  return money(Number(roundRatio(BigInt(m.amount) * BigInt(bp), 10000n)));
}

/** "1 234,56 zł" with U+00A0 as thousands separator and before "zł". */
export function formatMoney(m: Money): string {
  const abs = Math.abs(m.amount);
  const zl = Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const gr = (abs % 100).toString().padStart(2, '0');
  return `${m.amount < 0 ? '-' : ''}${zl},${gr} zł`;
}
